import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRecentIds, setRecentIds } from '../components/elements/recentElements';

const MAX_RECENTS = 30;

const normalizeRecentItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (!item) return null;
      const catalogId = typeof item.catalogId === 'string' ? item.catalogId : item.id;
      if (!catalogId) return null;
      const usedAtRaw = item.usedAt;
      const usedAt = typeof usedAtRaw === 'number'
        ? usedAtRaw
        : usedAtRaw?.toMillis
          ? usedAtRaw.toMillis()
          : Date.now();
      return { catalogId, usedAt };
    })
    .filter(Boolean);
};

const sortRecentItems = (items) =>
  [...items].sort((a, b) => (b.usedAt || 0) - (a.usedAt || 0));

const buildRecentItems = (catalogId, prevItems) => {
  const now = Date.now();
  const next = [
    { catalogId, usedAt: now },
    ...prevItems.filter((item) => item.catalogId !== catalogId),
  ];
  return next.slice(0, MAX_RECENTS);
};

const getRecentDocRef = (firebase, franchiseeId) =>
  firebase?.db
    ?.collection('franchisees')
    ?.doc(franchiseeId)
    ?.collection('studioMeta')
    ?.doc('recentElements');

const useRecentElements = (firebase, franchiseeId) => {
  const [recentItems, setRecentItems] = useState(() =>
    getRecentIds().map((id) => ({ catalogId: id, usedAt: Date.now() })),
  );
  const [error, setError] = useState('');
  const isReadyRef = useRef(false);

  useEffect(() => {
    if (!firebase || !franchiseeId) {
      setRecentItems(getRecentIds().map((id) => ({ catalogId: id, usedAt: Date.now() })));
      return undefined;
    }

    const ref = getRecentDocRef(firebase, franchiseeId);
    if (!ref) return undefined;

    const unsubscribe = ref.onSnapshot(
      (snapshot) => {
        const data = snapshot.data() || {};
        const nextItems = sortRecentItems(normalizeRecentItems(data.items));
        setRecentItems(nextItems);
        setRecentIds(nextItems.map((item) => item.catalogId));
        isReadyRef.current = true;
        setError('');
      },
      (err) => {
        console.error('Failed to load recent elements:', err);
        setError(err?.message || 'Unable to load recent elements.');
        if (!isReadyRef.current) {
          setRecentItems(getRecentIds().map((id) => ({ catalogId: id, usedAt: Date.now() })));
        }
      },
    );

    return () => unsubscribe();
  }, [firebase, franchiseeId]);

  const pushRecent = useCallback(
    (catalogId) => {
      if (!catalogId) return;
      setRecentItems((prev) => {
        const next = buildRecentItems(catalogId, prev);
        setRecentIds(next.map((item) => item.catalogId));
        if (firebase && franchiseeId) {
          const ref = getRecentDocRef(firebase, franchiseeId);
          ref?.set(
            {
              items: next,
              updatedAt: firebase.fieldValue?.serverTimestamp
                ? firebase.fieldValue.serverTimestamp()
                : new Date(),
            },
            { merge: true },
          ).catch((err) => {
            console.error('Failed to update recent elements:', err);
            setError(err?.message || 'Unable to update recent elements.');
          });
        }
        return next;
      });
    },
    [firebase, franchiseeId],
  );

  const recentIds = useMemo(
    () => recentItems.map((item) => item.catalogId),
    [recentItems],
  );

  return {
    recentItems,
    recentIds,
    pushRecent,
    error,
  };
};

export default useRecentElements;
